import { Fragment, useCallback, useMemo, useState } from "react";
import { useSupercluster } from "./hooks/useSupercluster";
import { FeatureCollection, GeoJsonProperties, Point } from "geojson";
import Supercluster, { ClusterProperties } from "supercluster";
import { ClusterMarker } from "./ClusterMarker";
import { Listing } from "./ClusteredMarkers";
import { Marker as MarkerComponent } from "./MarkersTest";
import { Infowindow } from "./Infowindow";
import { Marker } from "@googlemaps/markerclusterer";
import { useDispatch, useSelector } from "react-redux";
import { addToFavorites, removeFromFavorites } from "../../services/api";
import {
  pushUserFavoritesData,
  setUserFavoritesData,
} from "../../store/reducers/listingReducer";
import { featureCollection, point } from "@turf/helpers";
import { useMap } from "@vis.gl/react-google-maps";
import { getPixelDistance, LatLngLiteral } from "./utils";
import { useLocation } from "react-router-dom";
import { CommercialMarker } from "./CommercialMarker";

const superclusterOptions: Supercluster.Options<
  GeoJsonProperties,
  ClusterProperties
> = {
  extent: 256,
  minPoints: 5,
  radius: 80,
  maxZoom: 16,
};

export const ClusterMarkers = ({
  listing,
  onAddToPrint,
  printPath,
}: {
  listing: any[];
  onAddToPrint: (listing: any) => void;
  printPath: any[];
}) => {
  const geojson = useMemo(() => convertToGeoJSON(listing), [listing]);

  const { clusters } = useSupercluster(geojson, superclusterOptions);

  const [markers, setMarkers] = useState<{ [key: string]: Marker }>({});
  const [selected, setSelected] = useState<any>(null);

  const location = useLocation();

  const appType = useSelector((state: any) => state.listingReducer.appType);

  const isCommercial = appType === "commercial";

  const userFavorites = useSelector(
    (state: any) => state.listingReducer.userFavorites
  );
  const logged = useSelector((state: any) => state.listingReducer.logged);
  const hoveredListing = useSelector(
    (state: any) => state.listingReducer.hoveredListing
  );
  const dispatch = useDispatch();
  const map = useMap();

  map?.addListener("click", () => {
    if (selected) {
      setSelected(null);
    }
  });

  const handleMarkerClick = (item: any, event: any) => {
    console.log("marker clicked");
    if (event.domEvent.ctrlKey || event.domEvent.metaKey) {
      onAddToPrint(item);
      return;
    }
    setSelected(item);
  };

  const handleInfoWindowClose = useCallback(() => {
    setSelected(null);
  }, []);

  const setMarkerRef = useCallback((marker: Marker | null, key: string) => {
    setMarkers((markers) => {
      //   if ((marker && markers[key]) || (!marker && !markers[key]))
      //     return markers;

      if (marker) {
        return { ...markers, [key]: marker };
      } else {
        const { [key]: _, ...newMarkers } = markers;

        return newMarkers;
      }
    });
  }, []);

  const isFavorited = useCallback(
    (listingFav: any) => {
      for (let i = 0; i < userFavorites.length; i++) {
        if (
          userFavorites[i].listing_unique_id === listingFav.listing_unique_id
        ) {
          return true; // Found the target listing_unique_id in the array
        }
      }
      return false;
    },
    [userFavorites]
  );

  const toggleFavorite = useCallback(
    async (listingFav: any) => {
      if (!logged) {
        return;
      }
      let params: any = {};
      params["listing_unique_id"] = listingFav.listing_unique_id;
      if (isFavorited(listingFav)) {
        let result = await removeFromFavorites(params);
        if (!result?.data.success) {
          return;
        }
        let newFavorites = userFavorites.filter(
          (item: any) => item.listing_unique_id !== listingFav.listing_unique_id
        );
        dispatch(setUserFavoritesData(newFavorites));
      } else {
        console.log("fav params", params);
        let result = await addToFavorites(params);
        if (!result?.data.success) {
          return;
        }
        if (result) {
          dispatch(pushUserFavoritesData(result?.data.data));
        }
      }
    },
    [dispatch, isFavorited, logged, userFavorites]
  );

  const pointInCluster = (point: LatLngLiteral, cluster: LatLngLiteral) => {
    const zoom = map?.getZoom();
    const radius = superclusterOptions.radius;
    if (zoom && radius) {
      const distanceInPixels = getPixelDistance(cluster, point, zoom);
      return distanceInPixels <= radius;
    }
    return false;
  };

  return (
    <>
      {clusters.map((feature) => {
        const [lng, lat] = feature.geometry.coordinates;
        let isClusterHovered = false;
        const clusterProperties = feature.properties as any;
        const isCluster: boolean = clusterProperties.cluster;

        if (isCluster && hoveredListing) {
          isClusterHovered = pointInCluster(
            {
              lat: parseFloat(hoveredListing.latitude),
              lng: parseFloat(hoveredListing.longitude),
            },
            { lat, lng }
          );
        }

        return isCluster ? (
          <ClusterMarker
            position={{ lat, lng }}
            isHovered={isClusterHovered}
            fill={isCommercial ? "#F04438" : "#16B364"}
          />
        ) : (
          <Fragment key={clusterProperties.listing_unique_id}>
            {isCommercial ? (
              <CommercialMarker
                listing={clusterProperties}
                onClick={handleMarkerClick}
                setMarkerRef={setMarkerRef}
              />
            ) : (
              <MarkerComponent
                key={clusterProperties.listing_unique_id}
                listing={clusterProperties}
                type="new"
                setMarkerRef={setMarkerRef}
                onClick={handleMarkerClick}
                selected={Boolean(
                  (selected &&
                    selected.listing_unique_id ===
                      clusterProperties.listing_unique_id) ||
                    printPath.includes(clusterProperties.listing_unique_id)
                )}
              />
            )}

            {selected &&
              selected.listing_unique_id ===
                clusterProperties.listing_unique_id && (
                <Infowindow
                  anchor={markers[selected?.listing_unique_id]}
                  handleInfoWindowClose={handleInfoWindowClose}
                  isFavorited={isFavorited(clusterProperties)}
                  selected={selected}
                  toggleFavorite={() => toggleFavorite(clusterProperties)}
                  isCommercial={isCommercial}
                />
              )}
          </Fragment>
        );
      })}
    </>
  );
};

function convertToGeoJSON(listing: any[]): FeatureCollection<Point, any> {
  const points = listing.map((itm) =>
    point([itm.longitude, itm.latitude], itm)
  );
  const geojson = featureCollection(points);
  return geojson;
}
